import { toInteger } from 'lodash';
import { waterMarkBridge } from '@meitu/wasm-lib';
import { loadImageAsync } from '@/utils/file-kit';
import { WATERMARK_TEXTS_MAX } from '@/version-types';

/**
 * 生成图片水印属性
 */
interface ICreateWaterMark {
    /**
     * 画布宽度
     */
    width: number;

    /**
     * 画布高度
     */
    height: number;

    /**
     * Canvas 画布元素对象
     */
    canvas: HTMLCanvasElement;

    /**
     * 水印图片宽度
     */
    imgWidth: number;

    /**
     * 水印图片高度
     */
    imgHeight: number;

    /**
     * 水印图片地址
     */
    imgSrc: string;
}

/**
 * 生成图片水印
 *
 * @param content - 水印内容属性
 */
export const createWaterMarkAsync = async (content: ICreateWaterMark) => {
    const { width, height, canvas, imgWidth, imgHeight, imgSrc } = content;
    const ctx = canvas.getContext('2d');
    const column = Math.trunc(height / imgHeight) + 1;
    const row = Math.trunc(width / imgWidth) + 1;

    const img = await loadImageAsync(imgSrc);

    for (let i = 0; i < column; i++) {
        for (let j = 0; j < row; j++) {
            ctx?.drawImage(img, imgWidth * j, imgHeight * i, imgWidth, imgHeight);
        }
    }

    return canvas;
};

export const computeFullScreenWatermarkSize = (
    scale: number,
    image: { width: number; height: number },
    stage: { width: number; height: number },
) => {
    // const DEFAULT = 0.5; //默认放大1.3倍
    let width = 0;
    let height = 0;
    if (stage.width > stage.height) {
        const s = image.width / image.height;
        width = toInteger(image.width * scale);
        height = toInteger(width / s);
    } else {
        const s = image.height / image.width;
        height = toInteger(image.height * scale);
        width = toInteger(height * s);
    }
    if ((image.width > image.height && width < height) || (image.height > image.width && height < width)) {
        const x = height;
        height = width;
        width = x;
    }
    return {
        width,
        height,
    };
};

export const clearTextWatermark = async (id) => {
    for (let i = WATERMARK_TEXTS_MAX - 1; i >= 0; i--) {
        await waterMarkBridge.removeText(id, i);
    }
};

/**
 * 在 watermark bridge 中创建水印
 *
 * @param id - 水印 ID
 */
export const createWatermark = async (id: string) => {
    await waterMarkBridge.removeWaterMark(id);
    await waterMarkBridge.addWaterMark(id);
    return true;
};

/**
 * 同步图片到 watermark bridge
 *
 * @param id - 水印 ID
 * @param url - 图片地址
 */
export const setImgWatermark = async (id: string, url: string) => {
    await waterMarkBridge.setCustomizePath(id, url);
    return true;
};

/**
 * 注册字体
 *
 * @param fontName - 字体名称
 * @param fontPathZip - 字体 Zip 包链接地址
 */
export const registerFont = async (fontName: string, fontPathZip: string) => {
    await waterMarkBridge.registerFont(fontName, fontPathZip);
    return true;
};

/**
 * 同步文本到 watermark bridge
 *
 * @param id - 水印 ID
 * @param zipUlr - 字体 Zip 包文件链接地址
 */
export const setTextWatermark = async (id: string, zipUlr: string) => {
    const materialId = zipUlr.match(/zip\/(.*?).zip/)![1];
    await waterMarkBridge.setMaterialPath(id, materialId, zipUlr, materialId);
    return true;
};

/**
 * 从 watermark bridge 中获取水印图片
 *
 * @param id - 水印 ID
 */
export const getWatermarkImg = async (id: string) => {
    const result = await waterMarkBridge.getWaterMarkSplitBitmap(id);
    return result;
};

/**
 * 计算水印位置
 *
 * @param imgSize - 水印图片尺寸
 * @param stageSize - 舞台尺寸
 */
export const computedPosition = (
    imgSize: { width: number; height: number },
    stageSize: { height: number; width: number },
) => {
    let imgWidth: number = 0;
    let imgHeight: number = 0;
    const scale = imgSize.width / imgSize.height;
    if (stageSize.height > stageSize.width) {
        imgWidth = stageSize.width / 4;
        imgHeight = imgWidth / scale;
    } else {
        imgHeight = stageSize.height / 4;
        const scale = imgSize.width / imgSize.height;
        imgWidth = imgHeight * scale;
    }
    const top = (stageSize.height - imgHeight) * 0.9;
    const left = (stageSize.width - imgWidth) * 0.9;
    return {
        width: imgWidth,
        height: imgHeight,
        top,
        left,
    };
};
